import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { getInsufficientLivesMessage } from '../utils/livesErrorI18n';

interface LivesGateModalProps {
  show: boolean;
  onHide: () => void;
  /**
   * Body text. When <see cref="showVipUpsell"/> is true and this is empty, uses
   * `lives.noLivesVipBody`. Otherwise falls back to generic insufficient-lives copy for legacy mode.
   */
  message?: string;
  /**
   * When true (default): title + VIP upsell, Cancel + Become VIP.
   * When false: legacy "Error" modal with a single primary (e.g. Back to home).
   */
  showVipUpsell?: boolean;
  /** If set, VIP button runs this instead of navigate('/vip') + onHide (e.g. quiz start error). */
  onVipNavigate?: () => void;
  /** Legacy single-button label when showVipUpsell is false */
  primaryLabel?: string;
}

/**
 * Shared modal for blocked play due to lives (solo catalog, solo player, PvP lobby, PvP room).
 */
const LivesGateModal: React.FC<LivesGateModalProps> = ({
  show,
  onHide,
  message,
  showVipUpsell = true,
  onVipNavigate,
  primaryLabel,
}) => {
  const { t } = useSafeTranslation();
  const navigate = useNavigate();

  const handleGoVip = () => {
    if (onVipNavigate) {
      onVipNavigate();
    } else {
      navigate('/vip');
      onHide();
    }
  };

  if (showVipUpsell) {
    const body = message?.trim()
      ? message
      : t(
          'lives.noLivesVipBody',
          'Twice the life capacity and half the wait between recoveries while your VIP is active.'
        );
    return (
      <Modal show={show} onHide={onHide} centered backdrop="static" keyboard>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('lives.noLivesVipTitle', 'Out of lives')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">{body}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 d-flex flex-wrap gap-2 justify-content-end">
          <Button variant="outline-secondary" onClick={onHide}>
            {t('lives.cancelPlay', 'Cancel')}
          </Button>
          <Button variant="warning" className="text-dark" onClick={handleGoVip}>
            {t('lives.goVip', 'Become VIP')}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const body = message?.trim() ? message : getInsufficientLivesMessage(t);
  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" keyboard>
      <Modal.Header closeButton>
        <Modal.Title>{t('common.error', 'Error')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-0">{body}</p>
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-danger" onClick={onHide}>
          {primaryLabel ?? t('common.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LivesGateModal;
